import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, Router, UrlTree } from "@angular/router";
import { Observable, of } from "rxjs";
import { catchError, map, take } from "rxjs/operators";
import { CapabilityString } from "../directives/capabilities/capabilities-params.model";
import { CapabilitiesHttpService } from "../services/capabilities/capabilities.http.service.service";
import { CapabilitySystemResponse } from "../services/capabilities/models/capability-system-response.model";

@Injectable({
  providedIn: "root"
})
export class CapabilityGuard {
  constructor(
    private readonly capabilitiesHttpService: CapabilitiesHttpService,
    private readonly router: Router
  ) { }

  canActivate = (route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> => {
    const systemCapability = route.data["systemCapability"] as CapabilityString;
    const clientCapability = route.data["clientCapability"] as CapabilityString;

    return this.capabilitiesHttpService.getSystemCapabilities().pipe(
      take(1),
      map((response) =>
        this.hasAnyRequiredCapability(response, systemCapability, clientCapability)
          ? true
          : this.router.parseUrl("/not-found")
      ),
      catchError(() => of(this.router.parseUrl("/not-found")))
    );
  }

  private hasAnyRequiredCapability(
    response: CapabilitySystemResponse,
    systemCapability?: CapabilityString,
    clientCapability?: CapabilityString
  ): boolean {
    if (!systemCapability && !clientCapability) {
      return false;
    }

    const hasSystemCapability =
      !!systemCapability &&
      (response?.systemCapabilities?.includes(systemCapability) ?? false);
    const hasClientCapability =
      !!clientCapability &&
      (response?.clientCapabilities?.includes(clientCapability) ?? false);

    return hasSystemCapability || hasClientCapability;
  }
}
